<script setup lang="ts">
import MessageLayout from '@/components/assistant/chat/message/MessageLayout.vue'
import UsageLimitMessage from '@/components/assistant/chat/UsageLimitMessage.vue'
import type {Message, SystemMessage, UserMessageContent} from '@/client/mentor/MessagesClient'
import {ConversationMessageType} from '@/client/mentor/MessagesClient'
import {ErrorCode} from '@/client/mentor/types'
import {assistantChatMessageContextKey} from '@/components/assistant/chat/messageContext'
import {computed, inject} from 'vue'

const WARNING_STATES: string[] = [
  ErrorCode.NOT_ENOUGH_CREDITS,
  ErrorCode.TOO_MANY_PENDING_CREDITS,
  ErrorCode.TOO_MANY_PENDING_MESSAGES,
]

const ERROR_CODE_KEY_SUFFIX_MAP: Record<string, string> = {
  [ErrorCode.TOO_MANY_PENDING_CREDITS]: 'tooManyPendingMessages',
  [ErrorCode.TOO_MANY_PENDING_MESSAGES]: 'tooManyPendingMessages',
}

defineEmits<{
  selectPreviousMessage: [content: UserMessageContent]
}>()

const props = defineProps<{
  disabled?: boolean
  message: SystemMessage
  previousMessage?: Message
}>()

const messageContext = inject(assistantChatMessageContextKey)

const creditUsage = computed(() => messageContext?.creditUsage.value)
const errorCodeSuffix = computed(() => ERROR_CODE_KEY_SUFFIX_MAP[props.message.content.errorCode] ?? 'unknownError')
const isWarningState = computed(() => WARNING_STATES.includes(props.message.content.errorCode))
</script>

<template>
  <div class="flex flex-col gap-2">
    <MessageLayout
      :class="
        isWarningState
          ? 'bg-new-yellow-500/10 border-new-sm border-yellow-500/20'
          : 'bg-new-red-500/10 border-new-sm border-new-red-500/20'
      "
      :message="message"
    >
      <div class="flex flex-col gap-0 text-new-neutral-700">
        <UsageLimitMessage v-if="message.content.errorCode === ErrorCode.NOT_ENOUGH_CREDITS" :credit-usage="creditUsage" />
        <template v-else>
          <span class="font-bold" :class="{'text-new-red-500': !isWarningState}">{{
            $t(`assistant.chat.message.error.titles.${errorCodeSuffix}`)
          }}</span>
          <p v-html="$t(`assistant.chat.message.error.descriptions.${errorCodeSuffix}`)"></p>
        </template>
      </div>
    </MessageLayout>

    <template v-if="previousMessage && previousMessage.type === ConversationMessageType.USER">
      <button
        class="w-fit px-2.5 py-1 rounded-lg text-left text-sm bg-new-neutral-0 text-new-neutral-700 hover:text-new-secondary-default disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="disabled"
        type="button"
        @click="$emit('selectPreviousMessage', previousMessage.content)"
      >
        {{ $t('assistant.chat.message.error.actions.retryPrevious') }}
      </button>
    </template>
  </div>
</template>
